import type { PageItem } from './pages-json'
// pages.json 消费统一走这里（默认导入 + 解构兜底，避免 h5 端缺失 tabBar/subPackages 时 ESM 具名导入报错）
import { pages, subPackages, tabBar } from './pages-json'
// getImageUrl 内部要用 isH5 判断，直接引入（下面的 export * 只负责对外转发）
import { isH5 } from './platform'

// 自定义导航尺寸（pxToRpx / statusBarHeight / navHeight / navTop / navTopPx）统一从 @/utils 出口
export * from './custom-nav'
// 列表筛选查询参数拼装（buildHouseListQueryParams / buildEstateListQueryParams）
export * from './list-search-query'
// 统一页面跳转（routerPush / routerBack），规避页面栈 10 层上限
export * from './navigator'
// 汉字拼音首字母（getPinyinFirstLetter）
export * from './pin-yin'
// 平台判断（platform / isH5 / isApp / isMp，编译期常量 __UNI_PLATFORM__）
export * from './platform'

/**
 * 获取当前页面栈中的最后一个页面实例（即当前正在显示的页面）
 *
 * 注：不用 `getCurrentPages().at(-1)`，低版本安卓打包会报错，
 * 故改用下标 `pages[pages.length - 1]` 取值。
 * getCurrentPages() 至少有 1 个元素，无需额外判空。
 */
function getLastPage() {
  const pages = getCurrentPages()
  return pages[pages.length - 1]
}

/**
 * 判断当前页面是否为 tabBar 页（首页 / 工作台 / 我的 等底部导航页）
 * @returns 当前页命中 tabBar.list 时返回 true
 */
export function getIsTabbar(): boolean {
  // 未配置 tabBar 或 list 为空，直接视为非 tabBar 页
  if (!tabBar || !tabBar.list.length) {
    return false
  }
  const currPath = getLastPage().route
  return tabBar.list.some(item => item.pagePath === currPath)
}

/**
 * 获取当前页面路由信息，返回 { path, query }
 *
 * - path  如 `/pages/login/index`
 * - query 如 `{ redirect: '/pages/demo/base/route-interceptor' }`
 *
 * 说明：页面实例上只有 `$page.fullPath` 靠谱，
 * 其余（options / $page.options 等）取值不稳定，故统一取 fullPath。
 */
export function currRoute() {
  const lastPage = getLastPage()
  const { fullPath } = (lastPage as any).$page as { fullPath: string }
  // eg: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
  return getUrlObj(fullPath)
}

/**
 * 递归 decode，直到字符串不再以 `%` 开头
 * 用于兼容参数被多重 encode 的情况（如 redirect）
 */
function ensureDecodeURIComponent(url: string): string {
  if (url.startsWith('%')) {
    try {
      return ensureDecodeURIComponent(decodeURIComponent(url))
    }
    catch {
      // 畸形 % 转义（如 %zz）decodeURIComponent 会抛 URIError，原样返回兜底
      return url
    }
  }
  return url
}

/**
 * 解析 url，拆出 path 与 query
 *
 * @example
 * getUrlObj('/pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor')
 * // => { path: '/pages/login/index', query: { redirect: '/pages/demo/base/route-interceptor' } }
 */
export function getUrlObj(url: string): { path: string, query: Record<string, string> } {
  const [path, queryStr] = url.split('?')
  if (!queryStr) {
    return { path, query: {} }
  }
  const query: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    if (!item)
      return
    // 按第一个 = 切分：兼容无值参数（如 ?flag）与值本身含 = 的情况
    const idx = item.indexOf('=')
    const key = idx === -1 ? item : item.slice(0, idx)
    const rawVal = idx === -1 ? '' : item.slice(idx + 1)
    // 统一 decode 一次
    query[key] = ensureDecodeURIComponent(rawVal)
  })
  return { path, query }
}

/**
 * 获取主包 + 分包的所有页面，path 会补成以 `/` 开头的绝对路径
 *
 * @param key 过滤字段，默认 `needLogin`（与页面 `definePage()` 里的 needLogin 配对）。
 *            传空字符串则不过滤，返回全部页面。
 */
export function getAllPages(key = 'needLogin'): PageItem[] {
  // 主包页面：path 补成绝对路径
  const mainPages = pages
    .filter(page => !key || page[key])
    .map(page => ({ ...page, path: `/${page.path}` }))

  // 分包页面：path 需再拼上分包 root（subPackages 类型已在 pages-json.ts 收口，h5 端为空数组）
  const subPages: PageItem[] = []
  subPackages.forEach((subPackage) => {
    const { root, pages: pageList } = subPackage
    pageList
      .filter(page => !key || page[key])
      .forEach((page) => {
        subPages.push({ ...page, path: `/${root}/${page.path}` })
      })
  })

  return [...mainPages, ...subPages]
}

/**
 * 需要登录的页面 path 数组（调用时实时计算）
 * 与常量 needLoginPages 结果一致，区别仅在求值时机
 */
export function getNeedLoginPages(): string[] {
  return getAllPages('needLogin').map(page => page.path)
}

/**
 * 需要登录的页面 path 数组（模块加载时求值的常量）
 * 与函数 getNeedLoginPages() 结果一致
 */
export const needLoginPages: string[] = getAllPages('needLogin').map(page => page.path)

/**
 * 拼登录页 URL（登录页路径的唯一出处，业务代码勿再手拼）
 *
 * @param redirect 登录成功后的回跳地址（`/` 开头绝对路径，可带 query），
 *                 缺省不带 redirect（登录页默认回「我的」tab）
 */
export function buildLoginUrl(redirect?: string): string {
  const loginRoute = '/pages/login/index'
  return redirect ? `${loginRoute}?redirect=${encodeURIComponent(redirect)}` : loginRoute
}

/**
 * 生成指定长度的随机字符串（大小写字母 + 数字）
 * 注：非标准 UUID，仅作简单随机 id 使用
 * @param length 生成字符串的长度
 */
export function uuid(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * charset.length)
    result += charset[randomIndex]
  }
  return result
}

/** 图片 OSS 基础地址（VITE_IMAGE_BASEURL） */
export const imageBaseUrl = import.meta.env.VITE_IMAGE_BASEURL

// accessSync 是同步桥接调用，缓存结果保证同一路径只查一次
const packageFileExistsCache = new Map<string, boolean>()

/** 判断小程序代码包内是否存在该文件，path 以 `/` 开头（相对包根目录），仅 mp-weixin 可用 */
function hasPackageFile(path: string): boolean {
  let exists = packageFileExistsCache.get(path)
  if (exists === undefined) {
    try {
      uni.getFileSystemManager().accessSync(path)
      exists = true
    }
    catch {
      // accessSync 对不存在的文件抛错，视为不存在
      exists = false
    }
    packageFileExistsCache.set(path, exists)
  }
  return exists
}

/**
 * 获取图片完整地址：小程序端包内 static/images 下存在同名文件时用本地路径，否则回退 OSS（VITE_IMAGE_BASEURL）；h5 端恒走 OSS
 * 注意：webp 不在小程序代码包文件类型白名单内（上传会被过滤、accessSync 探测不到），
 * 包内静态图只用 png/jpg/gif/svg；webp 仅用于 OSS 网络图。
 * @param path 相对路径，可带可不带开头的 `/`，如 'search.png'、'/tabbar/home.png'
 */
export function getImageUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  // h5 端没有代码包文件系统（uni.getFileSystemManager 不存在），静态图一律走 OSS
  if (isH5)
    return `${imageBaseUrl}${normalized}`
  const localPath = `/static/images${normalized}`
  return hasPackageFile(localPath) ? localPath : `${imageBaseUrl}${normalized}`
}

/** 房源封面图：取首张、http 直链、相对路径走图片 OSS；无图用 v1 默认封面 */
export function resolveHousingCover(raw?: string): string {
  const fallback = 'https://hxyj-community.oss-cn-shanghai.aliyuncs.com/fc-mini-static/images/list_default_house.png'
  if (!raw?.trim())
    return fallback
  const first = raw.split(',')[0].trim()
  if (first.startsWith('http'))
    return first
  return getImageUrl(first.replace(/^\//, ''))
}
